"use client";

import React, { useState } from "react";
import ScrollReveal from "@/components/common/ScrollReveal";
import RingOrbit, { type OrbitDot } from "@/components/common/RingOrbit";
import { ACCORDION_PANEL_BG } from "@/components/common/accordion";

type StandardId = "international" | "national" | "industry";

interface Ring {
  /** Static artwork (track, disc, icon) without the arcs and the orbiting dots. */
  src: string;
  /** Size of the artwork's own Figma frame. */
  width: number;
  height: number;
  /** Places that frame in the diagram — Figma draws all three rotated. */
  transform: string;
  /** The two outer arcs, in the artwork's frame. They turn red when selected. */
  arcs: [string, string];
  /** Arc colour while not selected — Figma uses a different grey per ring. */
  idle: string;
  /** The small red rings on the track, in the artwork's frame. Always turning. */
  orbit: { cx: number; cy: number; dots: OrbitDot[] };
  /** Label anchor in diagram units: horizontal centre and top edge. */
  labelX: number;
  labelY: number;
}

interface Standard {
  id: StandardId;
  label: string[];
  intro: string;
  items: { term?: string; text: string }[];
  ring: Ring;
}

/**
 * Figma node 2765:39294 — the 831×568 frame the three rings and their labels
 * are laid out in. Every ring and label coordinate below is in this frame.
 */
const DIAGRAM_W = 831;
const DIAGRAM_H = 568;

const STANDARDS: Standard[] = [
  {
    id: "international",
    label: ["International", "Regulations"],
    intro:
      "ECQ helps organizations meet the standards their customers, regulators, and partners expect, wherever they operate. Our coverage spans three layers: international regulations, national regulations across our home markets, and the industry standards and frameworks that define good security practice. Whichever standard applies, our approach remains consistent: meet the requirement, and validate it against a real-world attack.",
    items: [
      {
        term: "SOC 2",
        text: "AICPA attestation on how you safeguard customer data (security, availability, confidentiality); the report enterprise buyers ask for before signing.",
      },
      {
        term: "HIPAA",
        text: "US federal law protecting health information (PHI) for healthcare providers, health plans, and the vendors that serve them.",
      },
      {
        term: "GDPR",
        text: "The EU’s data-protection law, with global reach for any organization handling the personal data of people in the EU.",
      },
    ],
    ring: {
      src: "/images/Compliance/standard-international.svg",
      width: 310,
      height: 297,
      transform: "translate(155 140.5) rotate(180) translate(-155 -148.5)",
      arcs: [
        "M220.817 273.202C220.593 273.317 220.368 273.424 220.144 273.539C200.452 283.373 178.197 288.913 154.633 288.913C74.2629 288.913 9.11329 224.48 9.11329 144.994",
        "M267.129 53.6836C287.77 78.5278 300.16 110.329 300.16 144.994C300.16 172.816 292.177 198.791 278.354 220.808C278.238 220.994 278.122 221.18 278.006 221.366",
      ],
      idle: "var(--color-diagram-track)",
      orbit: {
        cx: 153.86,
        cy: 143.98,
        dots: [
          { x: 269.72, y: 134.93, r: 9.63 },
          { x: 134.56, y: 258.04, r: 9.63 },
          { x: 39.21, y: 124.76, r: 9.63 },
        ],
      },
      labelX: 156,
      labelY: 298,
    },
  },
  {
    id: "national",
    label: ["National", "Regulations"],
    intro:
      "Operating in Southeast Asia requires meeting the requirements of local regulators, each with distinct cybersecurity and data-protection rules. With teams established in Singapore, Thailand, and Vietnam, ECQ understands these requirements first-hand and helps clients maintain compliance with confidence in every market served.",
    items: [
      { term: "Singapore", text: "MAS TRM, Cybersecurity Act" },
      { term: "Thailand", text: "BOT, SEC, OIC, PDPA, CII" },
      { term: "Vietnam", text: "PDPL" },
      { term: "Other", text: "CSL, DSL, PIPL" },
    ],
    ring: {
      src: "/images/Compliance/standard-national.svg",
      width: 310,
      height: 307,
      transform: "translate(420.5 325) rotate(90) translate(-155 -153.5)",
      // Figma exports these two as separately rotated images; the rotation is
      // baked into the path data here.
      arcs: [
        "M278.216 236.528C295.415 211.98 305.307 181.87 304.759 149.489C303.577 79.694 254.291 22.112 189.374 8.47",
        "M253.679 263.651C229 284.856 197.171 297.873 162.246 298.465",
      ],
      idle: "var(--color-diagram-line)",
      orbit: {
        cx: 154.06,
        cy: 151.6,
        dots: [
          { x: 270.07, y: 142.45, r: 9.7 },
          { x: 134.72, y: 267.14, r: 9.69 },
          { x: 39.21, y: 132.13, r: 9.7 },
        ],
      },
      labelX: 424,
      labelY: 514,
    },
  },
  {
    id: "industry",
    label: ["Industry", "Standards &", "Frameworks"],
    intro:
      "Beyond statutory requirements, industry standards set the benchmark for how security should be built and operated. ECQ guides organizations from gap assessment to certification readiness across the most widely recognized frameworks, spanning payments, cloud, operational technology, automotive, media, and AI with every engagement validated by our technical team so that certification reflects genuine resilience.",
    items: [
      {
        text: "PCI DSS, NIST, CSA STAR, ISO/IEC 27001, SWIFT Audit, IEC 62443, TISAX, AIMS, TPN, etc.",
      },
    ],
    ring: {
      src: "/images/Compliance/standard-industry.svg",
      width: 310,
      height: 322,
      transform: "translate(670 147) rotate(-90) translate(-155 -161)",
      // Figma outlines these arcs (2px idle, 3px selected); these are their
      // centre lines, stroked at one width like the other two rings.
      arcs: [
        "M155.535 23.294C235.618 21.934 301.649 86.028 303.021 166.447C303.571 198.629 293.68 228.554 276.481 252.953",
        "M251.939 279.898C227.262 300.977 195.433 313.919 160.508 314.511",
      ],
      idle: "var(--color-diagram-line)",
      orbit: {
        cx: 154.04,
        cy: 167.57,
        dots: [
          { x: 270.06, y: 158.45, r: 9.67 },
          { x: 134.71, y: 282.39, r: 9.66 },
          { x: 39.22, y: 148.22, r: 9.67 },
        ],
      },
      labelX: 677,
      labelY: 297,
    },
  },
];

const pctX = (x: number) => `${(x / DIAGRAM_W) * 100}%`;
const pctY = (y: number) => `${(y / DIAGRAM_H) * 100}%`;

/**
 * Security Compliance › Standard Delivers — Figma nodes 2765:38953 (International),
 * 2767:39402 (National) and 2767:39831 (Industry), one frame per selected ring.
 *
 * Picking a ring (or its label) turns its arcs red, drops a connector from the
 * label to the panel and swaps the panel copy. The small red rings on every
 * track turn regardless of the selection. The breadcrumb, title and tab row come
 * from the shared defend layout.
 */
const StandardDelivers = () => {
  const [activeId, setActiveId] = useState<StandardId>("international");
  const active = STANDARDS.find((s) => s.id === activeId) ?? STANDARDS[0];

  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pt-2">
      {/* --gap is the space between the diagram and the panel; the connector
          below the selected label runs across it. Figma: 114px at 1440. */}
      <div className="relative mx-auto w-full max-w-[1260px] [--gap:40px] sm:[--gap:64px] lg:[--gap:114px] lg:pt-[30px]">
        <ScrollReveal direction="up" delay={0}>
          <div
            className="relative mx-auto w-full max-w-[831px]"
            style={{
              aspectRatio: `${DIAGRAM_W} / ${DIAGRAM_H}`,
              containerType: "inline-size",
            }}
          >
            <svg
              viewBox={`0 0 ${DIAGRAM_W} ${DIAGRAM_H}`}
              fill="none"
              className="absolute inset-0 h-full w-full overflow-visible"
              aria-hidden
            >
              {STANDARDS.map(({ id, ring }) => (
                // Pointer convenience only — the labels are the real controls.
                <g
                  key={id}
                  transform={ring.transform}
                  className="cursor-pointer"
                  onClick={() => setActiveId(id)}
                >
                  <image href={ring.src} width={ring.width} height={ring.height} />
                  {ring.arcs.map((d) => (
                    <path
                      key={d}
                      d={d}
                      strokeWidth={2}
                      strokeLinecap="round"
                      className="transition-[stroke] duration-300 motion-reduce:transition-none"
                      style={{
                        stroke: id === activeId ? "var(--color-primary)" : ring.idle,
                      }}
                    />
                  ))}
                  <RingOrbit {...ring.orbit} />
                </g>
              ))}
            </svg>

            {STANDARDS.map(({ id, label, ring }) => {
              const isActive = id === activeId;
              return (
                // Column from the label down to the panel, so the connector
                // always starts under the label however large the label renders.
                <div
                  key={id}
                  className="pointer-events-none absolute flex -translate-x-1/2 flex-col items-center"
                  style={{
                    left: pctX(ring.labelX),
                    top: pctY(ring.labelY),
                    bottom: "calc(-1 * var(--gap))",
                    // Figma's 170px column; the floor keeps "INTERNATIONAL" on
                    // one line once the type stops shrinking on a phone.
                    width: `max(${pctX(170)}, 104px)`,
                  }}
                >
                  <button
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setActiveId(id)}
                    className="pointer-events-auto cursor-pointer rounded-sm text-center font-[var(--font-avenir)] font-semibold uppercase leading-[1.35] tracking-[0.15px] text-text-highlight focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
                    // 20px at the full 831px frame, never under 11px.
                    style={{ fontSize: "clamp(11px, 2.4067cqw, 20px)" }}
                  >
                    {label.map((line) => (
                      <span key={line} className="block whitespace-nowrap">
                        {line}
                      </span>
                    ))}
                  </button>
                  {isActive && (
                    <span
                      aria-hidden
                      className="connector-draw mt-3 w-[2px] flex-1 origin-top bg-diagram-line sm:mt-4"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={150}>
          <div
            className="mt-[var(--gap)] rounded-[12px] px-5 py-6 sm:p-8 lg:rounded-[16px] lg:p-12"
            style={{ background: ACCORDION_PANEL_BG }}
          >
            {/* Re-keyed so the copy fades in again on every selection. */}
            <div
              key={active.id}
              className="flex flex-col gap-6 text-text-highlight animate-[fade-in_300ms_ease-out] motion-reduce:animate-none lg:gap-10"
            >
              <p className="body-1">{active.intro}</p>
              <ul className="flex flex-col">
                {active.items.map((item) => (
                  <li key={item.text} className="body-1 flex gap-4 lg:gap-[23px]">
                    {/* Centred on the first line whatever the line-height token
                        is at this breakpoint (28px desktop, 24px phone). */}
                    <span
                      aria-hidden
                      className="mt-[calc((var(--leading-body-1)-8px)/2)] size-2 shrink-0 rounded-full bg-primary"
                    />
                    <span>
                      {item.term && <span className="font-semibold">{item.term}: </span>}
                      {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ScrollReveal>
      </div>

      <style jsx>{`
        .connector-draw {
          animation: connectorDraw 450ms ease-out both;
        }
        @keyframes connectorDraw {
          from {
            transform: scaleY(0);
          }
          to {
            transform: scaleY(1);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .connector-draw {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
};

export default StandardDelivers;
